// Seção de MULTA ou JUROS no form: configuração inline expansível (switch liga
// a regra; desligada, ela fica fora do payload) com seletor visual do tipo.
import type { LucideIcon } from "lucide-react"
import type { Control } from "react-hook-form"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"

import type { CreatePaymentConfigurationValues } from "../schemas"
import { AMOUNT_TYPES } from "../schemas"

const prefix = "pages.paymentConfigurations"

// Cor semântica do tipo (violet = regra/lógica). O selecionado se distingue
// pelo radio marcado + borda forte, não só pela cor.
const TYPE_OPTION_CLASSES = "bg-violet-50 text-gray-900"

const isAmountType = (
  value: string
): value is (typeof AMOUNT_TYPES)[number] =>
  (AMOUNT_TYPES as readonly string[]).includes(value)

interface ChargeRuleSectionProps {
  control: Control<CreatePaymentConfigurationValues>
  name: "fine" | "interest"
  title: string
  description: string
  icon: LucideIcon
  className?: string
}

export const ChargeRuleSection = ({
  control,
  name,
  title,
  description,
  icon: Icon,
  className,
}: ChargeRuleSectionProps) => {
  const { t } = useTranslation()
  const enabledName =
    name === "fine" ? "fineEnabled" : "interestEnabled"
  const enabled = useWatch({ control, name: enabledName })
  const type = useWatch({ control, name: `${name}.type` })
  const switchId = `payment-configuration-${name}-enabled`
  const typeLabelId = `payment-configuration-${name}-type`
  const panelId = `payment-configuration-${name}-panel`

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          <Icon
            aria-hidden="true"
            className={cn(
              "mt-0.5 h-4 w-4",
              enabled
                ? "text-violet-600"
                : "text-muted-foreground/60"
            )}
          />
          <div>
            <label
              htmlFor={switchId}
              className="text-sm font-medium"
            >
              {title}
            </label>
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          </div>
        </div>
        <FormField
          control={control}
          name={enabledName}
          render={({ field }) => (
            <Switch
              id={switchId}
              checked={field.value}
              onCheckedChange={field.onChange}
              aria-controls={panelId}
            />
          )}
        />
      </div>

      {enabled && (
        <div
          id={panelId}
          className="space-y-4 rounded-lg border bg-violet-50/40 p-4"
        >
          <FormField
            control={control}
            name={`${name}.type`}
            render={({ field }) => (
              <FormItem>
                <p
                  id={typeLabelId}
                  className="text-sm font-medium"
                >
                  {t(`${prefix}.form.amountType`)}
                </p>
                {/* RadioGroup do Radix: setas e roving tabindex do padrão WAI-ARIA. */}
                <RadioGroup
                  value={field.value}
                  onValueChange={(value) => {
                    if (isAmountType(value))
                      field.onChange(value)
                  }}
                  aria-labelledby={typeLabelId}
                  className="flex flex-wrap gap-3"
                >
                  {AMOUNT_TYPES.map((option) => {
                    const selected = field.value === option
                    const optionId = `${typeLabelId}-${option}`
                    return (
                      <label
                        key={option}
                        htmlFor={optionId}
                        className={cn(
                          "flex min-w-[130px] flex-1 cursor-pointer items-start gap-2 rounded-xl border-2 p-3 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring motion-safe:transition-all",
                          TYPE_OPTION_CLASSES,
                          // Sem opacidade: o estado é a borda + o radio,
                          // e o texto mantém contraste AA nos dois estados.
                          selected
                            ? "border-violet-500"
                            : "border-violet-200 hover:border-violet-300"
                        )}
                      >
                        <RadioGroupItem
                          id={optionId}
                          value={option}
                          className="mt-0.5"
                        />
                        <span className="flex flex-col gap-1">
                          <span className="text-sm font-semibold">
                            {t(
                              `${prefix}.amountTypes.${option}.label`
                            )}
                          </span>
                          <span className="text-[11px] text-gray-600">
                            {t(
                              `${prefix}.amountTypes.${option}.description`
                            )}
                          </span>
                        </span>
                      </label>
                    )
                  })}
                </RadioGroup>
              </FormItem>
            )}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={control}
              name={`${name}.amount`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {type === "MONETARY"
                      ? t(`${prefix}.form.amountMonetary`)
                      : t(
                          `${prefix}.form.amountPercentage`
                        )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      inputMode="decimal"
                      autoComplete="off"
                      className="tabular-nums"
                      placeholder={
                        type === "MONETARY"
                          ? t(
                              `${prefix}.form.placeholderMonetary`
                            )
                          : t(
                              `${prefix}.form.placeholderPercentage`
                            )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`${name}.daysAfterDue`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${prefix}.form.chargeDaysAfterDue`)}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      inputMode="numeric"
                      autoComplete="off"
                      className="tabular-nums"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
    </div>
  )
}
